/**
 * 📊 ONBOARDING SERVICE - Track user onboarding funnel across all roles
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';


export interface OnboardingStage {
  name: string;
  count: number;
  percentage: number;
}

export interface RoleOnboardingData {
  stages: OnboardingStage[];
  averageCompletion?: number;
}

export interface OnboardingFunnelData {
  driver: RoleOnboardingData;
  host: RoleOnboardingData;
  renter: RoleOnboardingData;
  period: string;
}

export const onboardingService = {

  /**
   * 📊 Get onboarding funnel analytics for all roles
   */
  async getOnboardingFunnel(): Promise<{
    success: boolean;
    data?: OnboardingFunnelData;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/onboarding/funnel`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error fetching onboarding funnel:', error);

      // Return empty data instead of mock data
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch onboarding funnel',
        data: {
          driver: {
            stages: [
              { name: 'Started Registration', count: 0, percentage: 100 },
              { name: 'Personal Info Complete', count: 0, percentage: 0 },
              { name: 'Documents Uploaded', count: 0, percentage: 0 },
              { name: 'Vehicle Info Complete', count: 0, percentage: 0 },
              { name: 'Verification Submitted', count: 0, percentage: 0 },
              { name: 'Fully Approved', count: 0, percentage: 0 }
            ],
            averageCompletion: 0
          },
          host: {
            stages: [
              { name: 'Started Registration', count: 0, percentage: 100 },
              { name: 'Profile Complete', count: 0, percentage: 0 },
              { name: 'Vehicle Registered', count: 0, percentage: 0 },
              { name: 'Awaiting Approval', count: 0, percentage: 0 },
              { name: 'Fully Approved', count: 0, percentage: 0 }
            ]
          },
          renter: {
            stages: [
              { name: 'Registered', count: 0, percentage: 100 },
              { name: 'Email Verified', count: 0, percentage: 0 },
              { name: 'First Login', count: 0, percentage: 0 },
              { name: 'Profile Completed', count: 0, percentage: 0 },
              { name: 'First Booking', count: 0, percentage: 0 }
            ]
          },
          period: 'Last 90 days'
        }
      };
    }
  },

  /**
   * 📈 Get onboarding summary statistics
   */
  async getOnboardingSummary(): Promise<{
    success: boolean;
    data?: {
      totalStarted: number;
      totalCompleted: number;
      overallCompletionRate: number;
      averageDaysToComplete: number;
      stuckUsers: number;
    };
    error?: string;
  }> {
    try {
      // Use funnel data to calculate summary
      const funnelResult = await this.getOnboardingFunnel();

      if (!funnelResult.success || !funnelResult.data) {
        throw new Error('Failed to fetch funnel data');
      }

      const { driver, host, renter } = funnelResult.data;

      // Calculate totals
      const totalStarted =
        (driver.stages[0]?.count || 0) +
        (host.stages[0]?.count || 0) +
        (renter.stages[0]?.count || 0);

      const totalCompleted =
        (driver.stages[driver.stages.length - 1]?.count || 0) +
        (host.stages[host.stages.length - 1]?.count || 0) +
        (renter.stages[renter.stages.length - 1]?.count || 0);

      const overallCompletionRate = totalStarted > 0
        ? Math.round((totalCompleted / totalStarted) * 100)
        : 0;

      // Estimate stuck users (started but not in final stage)
      const stuckUsers = totalStarted - totalCompleted;

      return {
        success: true,
        data: {
          totalStarted,
          totalCompleted,
          overallCompletionRate,
          averageDaysToComplete: 7, // TODO: Get from backend
          stuckUsers
        }
      };
    } catch (error) {
      console.error('❌ Error calculating onboarding summary:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to calculate summary',
        data: {
          totalStarted: 0,
          totalCompleted: 0,
          overallCompletionRate: 0,
          averageDaysToComplete: 0,
          stuckUsers: 0
        }
      };
    }
  }
};
